/**
 * Texture registry — every sprite key used in the game.
 *
 * RULE (per user spec 2026-08-04): every sprite must be a pure-white
 * rectangle placeholder at the dimensions declared in `assetManifest.ts`.
 * No procedural art, no decorative glyphs, no fan shapes, no rounded
 * bullets. When the user drops a real PNG into `data/graphics_user/<key>.png`
 * it overrides the placeholder at the same key.
 *
 * White-on-black palette: every sprite is a pure-white block on transparent
 * background (except where noted). The game canvas is also pure black, so
 * the sprites read as ink on a Nokia-3310-style screen.
 *
 * Per the user's design: there are exactly two enemy shapes in the game
 * right now — cardboard boxes (level 1) and aluminum cans (level 2).
 * Each is a plain white rectangle placeholder at the dimensions declared
 * in `src/game/assetManifest.ts`, ready to be replaced with real art.
 */
import Phaser from "phaser";

/**
 * Texture keys. Anything in `assets_user/<KEY>.png` is loaded by BootScene
 * via the fetch-and-register loader. Anything generated procedurally below
 * uses the same key, and `registerGameTextures()` is idempotent — safe to
 * call multiple times across scene restarts.
 */
export const TEX = {
  PLAYER: "player_newt",
  PLAYER_GODZILLA: "player_godzilla",
  SPLASH: "splash_trashstock",
  SUNGLASSES: "sunglasses",

  BULLET: "tex-bullet",
  BOSS_BULLET: "tex-boss-bullet",
  BEAM: "tex-beam",
  CHOMP: "tex_chomp",
  EXPLOSION: "tex-explosion",
  PICKUP: "tex-pickup",
  LIFE: "tex_life",
  HITPOINT: "tex_hitpoint",
  
  // Base keys for dynamic lookup, actual keys have _1, _2
  ENEMY_BIOWASTE: "enemy_biowaste",
  ENEMY_CARDBOARD: "enemy_cardboard",
  ENEMY_CAN: "enemy_can",
  ENEMY_PLASTIC: "enemy_plastic",
  ENEMY_EWASTE: "enemy_ewaste",
  ENEMY_GLASS: "enemy_glass",
  ENEMY_TOXIC: "enemy_toxic",
  ENEMY_RADIOACTIVE: "enemy_radioactive",
  ENEMY_TIRE: "enemy_tire",
  ENEMY_BATTERY: "enemy_battery",
  ENEMY_RAREEARTH: "enemy_rareearth",
  ENEMY_SATELLITE: "enemy_satellite",
  ENEMY_CHEMICALS: "enemy_chemicals",
} as const;

/**
 * Bake the procedural textures that aren't loaded from disk.
 *
 * Every placeholder is a plain white rectangle at the dimensions listed in
 * `src/game/assetManifest.ts`. When the user drops a PNG into the user
 * directory, BootScene's fetch-and-register overrides the procedural
 * texture at the same key, so the swap is automatic.
 *
 * Sizes mirror the manifest, not guesses:
 *   player_newt:        320x190
 *   boss_godzilla:      480x540
 *   splash_trashstock:  1024x1024
 *   enemy_cardboard:    64x64
 *   enemy_can:          56x72
 *   bullet:             8x8 (small white square — visually a dot)
 *   beam:               80x16 (wide white capsule)
 *   chomp:              24x24 (small white square — visible hit indicator)
 *   explosion:          16x16 (small white square)
 *   pickup:             32x32 (small white square capsule)
 */
export function registerGameTextures(scene: Phaser.Scene): void {
  // Player
  if (!scene.textures.exists(TEX.PLAYER)) {
    const w = 320, h = 190;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.PLAYER, w, h);
    g.destroy();
  }

  // Player Godzilla (godzilla placeholder)
  if (!scene.textures.exists(TEX.PLAYER_GODZILLA)) {
    const w = 320, h = 190;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.PLAYER_GODZILLA, w, h);
    g.destroy();
  }


  // Splash (menu title — generated as a white square at the manifest
  // size; user-art overrides this with their actual title image).
  if (!scene.textures.exists(TEX.SPLASH)) {
    const w = 1024, h = 1024;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.SPLASH, w, h);
    g.destroy();
  }

  // Bullet — small white square (8×8). User can replace with their art.
  if (!scene.textures.exists(TEX.BULLET)) {
    const w = 8, h = 8;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.BULLET, w, h);
    g.destroy();
  }

  // Boss Bullet — larger white circle (24x24) so it doesn't blend with stars
  if (!scene.textures.exists(TEX.BOSS_BULLET)) {
    const w = 24, h = 24;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillCircle(w / 2, h / 2, w / 2);
    g.generateTexture(TEX.BOSS_BULLET, w, h);
    g.destroy();
  }

  // Beam — horizontal white capsule (80×16). User can replace.
  if (!scene.textures.exists(TEX.BEAM)) {
    const w = 80, h = 16;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.BEAM, w, h);
    g.destroy();
  }

  // Chomp — white square matching the chomp range.
  if (!scene.textures.exists(TEX.CHOMP)) {
    const w = 200, h = 200;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.CHOMP, w, h);
    g.destroy();
  }

  // Explosion — small white square (16×16) shown on enemy death.
  if (!scene.textures.exists(TEX.EXPLOSION)) {
    const w = 16, h = 16;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.EXPLOSION, w, h);
    g.destroy();
  }

  // Enemy placeholders — pure white squares at the manifest dimensions.
  const PLACEHOLDER_SQUARES: Array<{ key: string; w: number; h: number }> = [
    { key: "tex_chomp", w: 200, h: 200 },
    { key: "enemy_biowaste_1",    w: 64, h: 64 },
    { key: "enemy_biowaste_2",    w: 64, h: 64 },
    { key: "enemy_biowaste_3",    w: 64, h: 64 },
    { key: "enemy_cardboard_1",   w: 64, h: 64 },
    { key: "enemy_cardboard_2",   w: 64, h: 64 },
    { key: "enemy_cardboard_3",   w: 64, h: 64 },
    { key: "enemy_can_1",         w: 56, h: 72 },
    { key: "enemy_can_2",         w: 56, h: 72 },
    { key: "enemy_can_3",         w: 56, h: 72 },
    { key: "enemy_plastic_1",     w: 50, h: 80 },
    { key: "enemy_plastic_2",     w: 50, h: 80 },
    { key: "enemy_plastic_3",     w: 50, h: 80 },
    { key: "enemy_ewaste_1",      w: 60, h: 60 },
    { key: "enemy_ewaste_2",      w: 60, h: 60 },
    { key: "enemy_ewaste_3",      w: 60, h: 60 },
    { key: "enemy_glass_1",       w: 40, h: 80 },
    { key: "enemy_glass_2",       w: 40, h: 80 },
    { key: "enemy_glass_3",       w: 40, h: 80 },
    { key: "enemy_toxic_1",       w: 50, h: 50 },
    { key: "enemy_toxic_2",       w: 50, h: 50 },
    { key: "enemy_toxic_3",       w: 50, h: 50 },
    { key: "enemy_radioactive_1", w: 50, h: 50 },
    { key: "enemy_radioactive_2", w: 50, h: 50 },
    { key: "enemy_radioactive_3", w: 50, h: 50 },
    { key: "enemy_tire_1",        w: 70, h: 70 },
    { key: "enemy_tire_2",        w: 70, h: 70 },
    { key: "enemy_tire_3",        w: 70, h: 70 },
    { key: "enemy_battery_1",     w: 30, h: 60 },
    { key: "enemy_battery_2",     w: 30, h: 60 },
    { key: "enemy_battery_3",     w: 30, h: 60 },
    { key: "enemy_rareearth_1",   w: 50, h: 50 },
    { key: "enemy_rareearth_2",   w: 50, h: 50 },
    { key: "enemy_rareearth_3",   w: 50, h: 50 },
    { key: "enemy_satellite_1",   w: 80, h: 80 },
    { key: "enemy_satellite_2",   w: 80, h: 80 },
    { key: "enemy_satellite_3",   w: 80, h: 80 },
    { key: "enemy_chemicals_1",   w: 40, h: 40 },
    { key: "enemy_chemicals_2",   w: 40, h: 40 },
    { key: "enemy_chemicals_3",   w: 40, h: 40 },
    { key: "tex_life",            w: 32, h: 32 },
    { key: "tex_hitpoint",        w: 32, h: 32 },
  ];
  for (const { key, w, h } of PLACEHOLDER_SQUARES) {
    if (scene.textures.exists(key)) continue;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  // Upgrade pickup — pure white square at 32×32 (user can override).
  if (!scene.textures.exists(TEX.PICKUP)) {
    const w = 32, h = 32;
    const g = scene.add.graphics();
    g.fillStyle(0xffffff, 1);
    g.fillRect(0, 0, w, h);
    g.generateTexture(TEX.PICKUP, w, h);
    g.destroy();
  }
}
